import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Center,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { getNameFromToken } from '../../util/IsAuth';
import { getAuth } from '../../util/APIHelp';

const Dashboard: React.FC = () => {
  const [dashboardContent, setDashboardContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const headingColor = useColorModeValue('blue.600', 'blue.300');
  const alertBg = useColorModeValue('red.50', 'red.900');
  const alertTextColor = useColorModeValue('red.700', 'red.200');

  useEffect(() => {
    const fetchDashboardContent = async () => {
      try {
        const response = await getAuth("/dashboard");
        if (response.ok) {
          const content = await response.text();
          setDashboardContent(content);
        } else {
          const errorText = await response.text();
          setError(`Failed to load dashboard content: ${response.status} - ${errorText}`);
        }
      } catch (e) {
        setError("An unexpected error occurred while fetching dashboard content.");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardContent();
  }, []);

  if (loading) {
    return (
      <Center height="100%">
        <VStack spacing={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
          <Text fontSize="lg" color={textColor}>Loading dashboard...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Flex
      minH="calc(100vh - 80px)"
      w="100%"
      justifyContent="center"
      alignItems="center"
      
      pt={4}
    >
      <Box
        p={{ base: 6, md: 8 }}
        maxWidth={{ base: '90%', md: '600px' }}
        w={'full'}
        bg={cardBg}
        boxShadow={'lg'}
        rounded={'lg'}
        textAlign="center"
        color={textColor}
      >
        {error ? (
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="lg"
            bg={alertBg}
            color={alertTextColor}
          >
            <AlertIcon boxSize="60px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Access Denied or Error
            </AlertTitle>
            <AlertDescription whiteSpace="wrap" wordBreak="break-word">
              {error}
              <br />
              Please ensure you have the correct permissions.
            </AlertDescription>
          </Alert>
        ) : (
          <VStack spacing={4}>
            <Heading as="h1" size="xl" color={headingColor}>
              Welcome to the Dashboard!
            </Heading>
            <Text fontSize="lg">
              {dashboardContent || "Welcome to the Aquatics Automation dashboard " + getNameFromToken() + "!"}
            </Text>
          </VStack>
        )}
      </Box>
    </Flex>
  );
};

export default Dashboard;